import { Component, ComponentInterface, Element, h, Host, JSX, Listen, Prop, State } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { ViewportObserver } from '../../utils/viewport-observer';
import { HorizontalScrollSize, HorizontalScrollMaskPosition } from './horizontal-scroll.types';
import { MASK_POSITION_END, MASK_POSITION_START } from './horizontal-scroll.constants';
import { DebounceDelays } from '../../shared/debounce-constants';
import { debounce } from '../../utils/debounce';

const DIRECTION_NEXT = 1;
const DIRECTION_PREVIOUS = -1;

/**
 * @slot - Contents of horizontal-scroll
 **/
@Component({
  tag: 'wb-horizontal-scroll',
  styleUrl: 'horizontal-scroll.scss',
  shadow: true,
})
export class HorizontalScrollComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Size of the horizontal scroll */
  @Prop({ reflect: true })
  public size: HorizontalScrollSize = 's';

  /** if present, allow to display content out of the mask. It will be required to provide the background-color: `--wb-horizontal-scroll-gradient-background`, if a non-standard background is used */
  @Prop()
  public disableClipping: boolean = false;

  /** if present, hides the arrow navigation buttons */
  @Prop()
  public hideArrowNavigation: boolean = false;

  /** sets aria-label for the button that scrolls towards the end */
  @Prop()
  public scrollToEndButtonTitle: string = 'scroll to end';

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** sets aria-label for the button that scrolls towards the start */
  @Prop()
  public scrollToStartButtonTitle: string = 'scroll to start';

  @State()
  private isScrolledToStart: boolean = true;

  @State()
  private isScrolledToEnd: boolean = true;

  @State()
  private maskPosition: HorizontalScrollMaskPosition = MASK_POSITION_END;

  @Listen('resize', { target: 'window' })
  public onResize(): void {
    this.isResizing = true;
    this.debounceIsntResizing();
  }

  private observeStartPixel: ViewportObserver;
  private observeEndPixel: ViewportObserver;
  private scrollerEl: HTMLElement;
  private startPixelEl: HTMLElement;
  private endPixelEl: HTMLElement;
  protected isResizing: boolean = false;

  private TagNames = transformTagNames(this, 'wb-horizontal-scroll', ['wb-icon', 'wb-round-button']);

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  public connectedCallback(): void {
    this.updateDirection();
  }

  public componentDidLoad(): void {
    this.attachViewportObservers();
  }

  public disconnectedCallback(): void {
    this.detachViewportObservers();
  }

  private attachViewportObservers(): void {
    const root = this.scrollerEl;
    // added to expand the box to the top so arrows/gradients are not visible when scrolled to the top or bottom of the screen
    const rootMargin = '4000px 0px 4000px 0px';

    this.observeStartPixel = new ViewportObserver({
      element: this.startPixelEl,
      root,
      rootMargin,
      viewportEnterFn: () => {
        this.maskPosition = MASK_POSITION_END;
        this.isScrolledToStart = true;
      },
      viewportLeaveFn: () => {
        this.isScrolledToStart = false;
      },
    });

    this.observeEndPixel = new ViewportObserver({
      element: this.endPixelEl,
      root,
      rootMargin,
      viewportEnterFn: () => {
        // Ensure mask is at the end when viewport intersects with end pixel while resizing
        this.maskPosition = this.isResizing ? MASK_POSITION_END : MASK_POSITION_START;
        this.isScrolledToEnd = true;
      },
      viewportLeaveFn: () => {
        this.maskPosition = MASK_POSITION_END;
        this.isScrolledToEnd = false;
      },
    });
  }

  private detachViewportObservers(): void {
    this.observeStartPixel?.disconnect();
    this.observeEndPixel?.disconnect();
  }

  private scrollToDirection(directionModifier: number): void {
    const rtlAdjustment = this.isRtl ? directionModifier : -directionModifier;
    const scrollLeft = this.scrollerEl.scrollLeft + (this.scrollerEl.clientWidth / 2) * rtlAdjustment;
    this.scrollerEl.scroll({ left: scrollLeft, behavior: 'smooth' });
  }

  private onScrollToEndClick = (): void => {
    this.scrollToDirection(DIRECTION_PREVIOUS);
  };

  private onScrollToStartClick = (): void => {
    this.scrollToDirection(DIRECTION_NEXT);
  };

  private debounceIsntResizing = debounce(() => (this.isResizing = false), DebounceDelays.Resize);

  private bindScrollerEl = (element: HTMLElement): void => {
    this.scrollerEl = element;
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    event.preventDefault();

    const actions: Record<string, () => void> = {
      ArrowRight: () => this.onScrollToEndClick(),
      ArrowLeft: () => this.onScrollToStartClick(),
      Home: () => this.scrollerEl?.scroll({ left: 0, behavior: 'smooth' }),
      End: () => {
        const max = (this.scrollerEl?.scrollWidth ?? 0) - (this.scrollerEl?.clientWidth ?? 0);
        this.scrollerEl?.scroll({ left: max, behavior: 'smooth' });
      },
    };

    const handler = actions[event.key];
    handler?.();
  };

  private bindStartPixelEl = (el: HTMLElement): void => {
    this.startPixelEl = el;
  };

  private bindEndPixelEl = (el: HTMLElement): void => {
    this.endPixelEl = el;
  };

  public render(): JSX.Element {
    const { WbIcon, WbRoundButton } = this.TagNames;

    return (
      <Host class="wb-horizontal-scroll">
        <div
          class={{
            container: true,
            'has-mask': !this.isScrolledToStart || !this.isScrolledToEnd,
            [`mask-${this.maskPosition}`]: true,
            'mask-full': !this.isScrolledToStart && !this.isScrolledToEnd,
          }}
        >
          <div class="inner" ref={this.bindScrollerEl} tabIndex={0} onKeyDown={this.onKeyDown}>
            <div class="pixel is-start" ref={this.bindStartPixelEl} />
            <slot />
            <div class="pixel is-end" ref={this.bindEndPixelEl} />
          </div>
        </div>
        {!this.hideArrowNavigation && !this.isScrolledToStart && (
          <WbRoundButton size={`new-${this.size}`} class="arrow is-start" onClick={this.onScrollToStartClick}>
            <WbIcon name="chevron-left/24" flip-rtl />
            {this.scrollToStartButtonTitle}
          </WbRoundButton>
        )}
        {!this.hideArrowNavigation && !this.isScrolledToEnd && (
          <WbRoundButton size={`new-${this.size}`} class="arrow is-end" onClick={this.onScrollToEndClick}>
            <WbIcon name="chevron-right/24" flip-rtl />
            {this.scrollToEndButtonTitle}
          </WbRoundButton>
        )}
      </Host>
    );
  }
}
